import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Label,
} from "@learn-me/ui";
import { adminApi } from "@/features/admin/api/adminApi";
import toast from "react-hot-toast";
import { UserX, Loader2, AlertTriangle } from "lucide-react";

interface DeactivateStudentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  studentId: string;
  studentName: string;
}

export function DeactivateStudentModal({
  open,
  onOpenChange,
  studentId,
  studentName,
}: DeactivateStudentModalProps) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");

  const deactivateMutation = useMutation({
    mutationFn: () =>
      adminApi.deactivateStudent(studentId, reason || undefined),
    onSuccess: () => {
      toast.success("Cuenta desactivada exitosamente");
      queryClient.invalidateQueries({
        queryKey: ["admin-student-detail-enhanced", studentId],
      });
      queryClient.invalidateQueries({ queryKey: ["admin-students-enhanced"] });
      setReason("");
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al desactivar la cuenta");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    deactivateMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <UserX className="h-5 w-5" />
            Desactivar Cuenta
          </DialogTitle>
          <DialogDescription>
            Esta accion desactivara la cuenta del estudiante.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">
                  ¿Estas seguro de desactivar a {studentName}?
                </p>
                <ul className="list-disc list-inside space-y-1 text-yellow-700">
                  <li>El estudiante no podra iniciar sesion</li>
                  <li>Se pausara el acceso a todos sus cursos</li>
                  <li>
                    Los accesos se reactivaran si activas la cuenta nuevamente
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Razon (opcional)</Label>
            <textarea
              id="reason"
              className="w-full min-h-[80px] px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motivo de la desactivacion..."
              maxLength={500}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={deactivateMutation.isPending}
            >
              {deactivateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Desactivando...
                </>
              ) : (
                <>
                  <UserX className="h-4 w-4 mr-2" />
                  Desactivar Cuenta
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
